import { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import {
  PlusIcon,
  TrashIcon,
  FolderIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline'
import {
  SkillApi,
  SkillPack,
  SkillTreeNode,
  TestAskAiSkillLink,
} from '../../services/skillApi'
import SkillTree from './SkillTree'

export type TestAskAiSkillScope = 'paper' | 'question'

interface TestAskAiSkillAttachProps {
  scope: TestAskAiSkillScope
  ownerId: number
  title?: string
  compact?: boolean
}

const TestAskAiSkillAttach = ({
  scope,
  ownerId,
  title,
  compact = false,
}: TestAskAiSkillAttachProps) => {
  const [links, setLinks] = useState<TestAskAiSkillLink[]>([])
  const [loading, setLoading] = useState(true)
  const [showPicker, setShowPicker] = useState(false)
  const [packs, setPacks] = useState<(SkillPack & { tree: SkillTreeNode[] })[]>([])
  const [selectedPackId, setSelectedPackId] = useState<number | null>(null)
  const [selectedFolder, setSelectedFolder] = useState<SkillTreeNode | null>(null)
  const [saving, setSaving] = useState(false)
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
  const [deleting, setDeleting] = useState(false)

  const heading =
    title ||
    (scope === 'paper' ? 'Skill Ask AI (bài test)' : 'Skill Ask AI (câu hỏi)')

  const loadLinks = async () => {
    try {
      setLoading(true)
      const data = await SkillApi.listTestAskAiSkillLinks(scope, ownerId)
      setLinks(data)
      setSelectedIds(new Set())
    } catch (e: any) {
      toast.error(e?.message || 'Không tải được skill Ask AI')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void loadLinks()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, ownerId])

  const allSelected = useMemo(
    () => links.length > 0 && selectedIds.size === links.length,
    [links, selectedIds]
  )
  const someSelected = selectedIds.size > 0

  const openPicker = async () => {
    try {
      setShowPicker(true)
      const data = await SkillApi.listPacksPicker()
      setPacks(data)
      setSelectedPackId(data[0]?.id ?? null)
      setSelectedFolder(null)
    } catch (e: any) {
      toast.error(e?.message || 'Không tải được thư viện skill')
      setShowPicker(false)
    }
  }

  const selectedPack = packs.find((p) => p.id === selectedPackId) || null

  const handleAttach = async () => {
    if (!selectedFolder) {
      toast.error('Chọn một thư mục / file')
      return
    }
    try {
      setSaving(true)
      if (selectedFolder.nodeType === 'FOLDER') {
        await SkillApi.addTestAskAiSkillLinks(scope, ownerId, {
          parentNodeId: selectedFolder.id,
        })
      } else {
        await SkillApi.addTestAskAiSkillLinks(scope, ownerId, {
          nodeIds: [selectedFolder.id],
        })
      }
      toast.success('Đã gắn skill cho Ask AI')
      setShowPicker(false)
      await loadLinks()
    } catch (e: any) {
      toast.error(e?.message || 'Gắn skill thất bại')
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteSelected = async () => {
    if (!someSelected) return
    if (!window.confirm(`Xóa ${selectedIds.size} liên kết skill?`)) return
    try {
      setDeleting(true)
      const deleted =
        selectedIds.size === links.length
          ? await SkillApi.bulkDeleteTestAskAiSkillLinks(scope, ownerId, { all: true })
          : await SkillApi.bulkDeleteTestAskAiSkillLinks(scope, ownerId, {
              linkIds: [...selectedIds],
            })
      toast.success(`Đã xóa ${deleted} liên kết`)
      await loadLinks()
    } catch (e: any) {
      toast.error(e?.message || 'Xóa thất bại')
    } finally {
      setDeleting(false)
    }
  }

  return (
    <div
      className={`rounded-xl border border-m2-outline-variant bg-m2-surface-low ${
        compact ? 'p-3' : 'p-4'
      }`}
    >
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <SparklesIcon className="h-5 w-5 shrink-0 text-m2-primary" />
          <div className="min-w-0">
            <h4 className={`font-semibold text-m2-on-surface ${compact ? 'text-sm' : 'text-base'}`}>
              {heading}
            </h4>
            {!compact && (
              <p className="text-xs text-m2-on-surface-variant">
                Skill này sẽ được gửi kèm khi học viên bấm Ask AI trên câu hỏi (sau khi nộp bài).
              </p>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {links.length > 0 && (
            <button
              type="button"
              onClick={handleDeleteSelected}
              disabled={!someSelected || deleting}
              className="inline-flex items-center gap-1 rounded-md border border-red-300 px-2.5 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50 disabled:opacity-40"
            >
              <TrashIcon className="h-3.5 w-3.5" />
              Xóa đã chọn
            </button>
          )}
          <button
            type="button"
            onClick={openPicker}
            className="inline-flex items-center gap-1 rounded-md border border-m2-primary/30 bg-m2-primary/10 px-2.5 py-1.5 text-xs font-medium text-m2-primary hover:bg-m2-primary/15"
          >
            <PlusIcon className="h-3.5 w-3.5" />
            Gắn skill
          </button>
        </div>
      </div>

      {loading ? (
        <p className="text-xs text-m2-on-surface-variant">Đang tải...</p>
      ) : links.length === 0 ? (
        <p className="rounded-lg border border-dashed border-m2-outline-variant px-3 py-4 text-center text-xs text-m2-on-surface-variant">
          Chưa gắn skill
        </p>
      ) : (
        <ul className="divide-y divide-m2-outline-variant overflow-hidden rounded-lg border border-m2-outline-variant bg-m2-surface-container">
          <li className="flex items-center gap-2 bg-m2-surface-high px-3 py-2 text-xs">
            <input
              type="checkbox"
              checked={allSelected}
              ref={(el) => {
                if (el) el.indeterminate = someSelected && !allSelected
              }}
              onChange={() => {
                if (allSelected) setSelectedIds(new Set())
                else setSelectedIds(new Set(links.map((l) => l.id)))
              }}
            />
            <span className="text-m2-on-surface-variant">
              {someSelected ? `Đã chọn ${selectedIds.size}/${links.length}` : `${links.length} liên kết`}
            </span>
          </li>
          {links.map((link) => (
            <li key={link.id} className="flex items-center gap-2 px-3 py-2">
              <input
                type="checkbox"
                checked={selectedIds.has(link.id)}
                onChange={() => {
                  setSelectedIds((prev) => {
                    const next = new Set(prev)
                    if (next.has(link.id)) next.delete(link.id)
                    else next.add(link.id)
                    return next
                  })
                }}
              />
              <FolderIcon className="h-4 w-4 shrink-0 text-amber-600" />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-m2-on-surface">
                  {link.title || link.nodeName}
                </div>
                <div className="truncate text-[11px] text-m2-on-surface-variant">
                  {link.packName} · {link.relativePath}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {showPicker && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="flex max-h-[90vh] w-full max-w-3xl flex-col rounded-lg bg-m2-surface-container shadow-xl">
            <div className="border-b border-m2-outline-variant px-5 py-4">
              <h2 className="text-lg font-semibold">Chọn skill gắn Ask AI</h2>
              <p className="mt-1 text-xs text-m2-on-surface-variant">
                Chọn thư mục hoặc file — nội dung sẽ được đưa vào context khi Ask AI.
              </p>
            </div>
            <div className="grid min-h-0 flex-1 gap-3 overflow-hidden p-4 md:grid-cols-[200px_1fr]">
              <div className="max-h-[50vh] overflow-y-auto rounded border border-m2-outline-variant">
                {packs.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    className={`block w-full truncate px-3 py-2 text-left text-sm hover:bg-m2-surface-high ${
                      selectedPackId === p.id ? 'bg-m2-primary/10 text-m2-primary' : ''
                    }`}
                    onClick={() => {
                      setSelectedPackId(p.id)
                      setSelectedFolder(null)
                    }}
                  >
                    {p.name}
                  </button>
                ))}
              </div>
              <div className="max-h-[50vh] overflow-y-auto rounded border border-m2-outline-variant">
                {selectedPack ? (
                  <SkillTree
                    nodes={selectedPack.tree || []}
                    selectedId={selectedFolder?.id}
                    onSelectFile={(n) => setSelectedFolder(n)}
                    onSelectFolder={(n) => setSelectedFolder(n)}
                  />
                ) : (
                  <p className="p-4 text-sm text-m2-on-surface-variant">Chọn pack</p>
                )}
              </div>
            </div>
            {selectedFolder && (
              <div className="border-t border-m2-outline-variant px-5 py-2 text-sm text-m2-on-surface">
                Đang chọn: <strong>{selectedFolder.name}</strong>
              </div>
            )}
            <div className="flex flex-wrap justify-end gap-2 border-t border-m2-outline-variant px-5 py-4">
              <button
                type="button"
                className="rounded-md border border-m2-outline px-4 py-2 text-sm"
                onClick={() => setShowPicker(false)}
                disabled={saving}
              >
                Hủy
              </button>
              <button
                type="button"
                className="rounded-md bg-m2-primary px-4 py-2 text-sm text-m2-on-primary disabled:opacity-50"
                disabled={saving || !selectedFolder}
                onClick={handleAttach}
              >
                Gắn skill
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default TestAskAiSkillAttach
